import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import { FiCreditCard, FiRefreshCw, FiEye, FiX } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const AdminPaymentTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifyingId, setVerifyingId] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [modalData, setModalData] = useState(null);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('payment_transactions')
      .select('*, orders(user_id, status, total_amount, paid_at)')
      .order('created_at', { ascending: false })
      .limit(50);
    if (data) setTransactions(data);
    setLoading(false);
  };

  const handleRetryVerification = async (tx) => {
    if (!tx.marx_transaction_reference) return alert("No Marx Transaction ID available to verify.");
    setVerifyingId(tx.id);
    try {
      const { data, error } = await supabase.functions.invoke('marx-verify', {
        body: { transactionReference: tx.marx_transaction_reference }
      });
      if (error) throw error;
      alert(`Verification Complete. Status: ${data.status}`);
      fetchTransactions();
    } catch (err) {
      alert('Verification failed: ' + err.message);
    } finally {
      setVerifyingId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'amount_mismatch': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'failed':
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiCreditCard} className="text-brand-darkSage w-6 h-6" />
          <h2 className="text-xl font-serif text-gray-800">Transaction Ledger</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchTransactions} className="flex items-center gap-2 text-xs bg-white border border-gray-200 hover:border-brand-gold text-gray-700 font-bold py-2 px-4 rounded-xl transition-colors shadow-sm">
            <SafeIcon icon={FiRefreshCw} /> Refresh
          </button>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-white text-gray-500 text-xs uppercase tracking-wider border-b border-gray-100">
              <th className="p-4 font-bold">Date & Time</th>
              <th className="p-4 font-bold">References</th>
              <th className="p-4 font-bold">Amount</th>
              <th className="p-4 font-bold">Status</th>
              <th className="p-4 font-bold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 text-sm">
            {loading ? (
              <tr><td colSpan="5" className="p-8 text-center text-gray-500">Loading ledger...</td></tr>
            ) : transactions.length === 0 ? (
              <tr><td colSpan="5" className="p-8 text-center text-gray-500">No transactions recorded.</td></tr>
            ) : (
              transactions.map(tx => (
                <tr key={tx.id} className="hover:bg-gray-50/80 transition-colors">
                  <td className="p-4 text-gray-600">
                    <div>{new Date(tx.created_at).toLocaleDateString()}</div>
                    <div className="text-xs text-gray-400">{new Date(tx.created_at).toLocaleTimeString()}</div>
                  </td>
                  <td className="p-4">
                    <div className="font-mono text-gray-800 text-xs" title="Merchant Ref">{tx.merchant_reference}</div>
                    <div className="text-[10px] text-gray-400 mt-1 font-mono" title="Marx Txn ID">{tx.marx_transaction_reference || 'N/A'}</div>
                  </td>
                  <td className="p-4 font-bold text-gray-800">{tx.currency} {tx.amount.toFixed(2)}</td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border ${getStatusBadge(tx.status)}`}>
                      {tx.status}
                    </span>
                    {tx.orders?.paid_at && <div className="text-[10px] text-gray-400 mt-1">Verified</div>}
                  </td>
                  <td className="p-4 text-right space-x-2">
                    <button onClick={() => setModalData(tx)} className="text-gray-400 hover:text-brand-gold p-2 transition-colors" title="View JSON">
                      <SafeIcon icon={FiEye} size={16} />
                    </button>
                    {tx.status === 'pending' && (
                      <button 
                        onClick={() => handleRetryVerification(tx)} 
                        disabled={verifyingId === tx.id || !tx.marx_transaction_reference}
                        className="text-xs bg-brand-darkSage/5 hover:bg-brand-darkSage/10 text-brand-darkSage font-bold py-1.5 px-3 rounded-lg transition-colors disabled:opacity-50 border border-brand-darkSage/10"
                      >
                        {verifyingId === tx.id ? '...' : 'Verify'}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl relative">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <SafeIcon icon={FiTerminal} /> Gateway Response Payload
              </h3>
              <button onClick={() => setModalData(null)} className="text-gray-400 hover:text-gray-800">
                <SafeIcon icon={FiX} size={20} />
              </button>
            </div>
            <div className="p-6 bg-[#1e1e1e] overflow-auto max-h-[70vh]">
              <pre className="text-green-400 text-xs font-mono">
                {JSON.stringify(modalData.gateway_response, null, 2) || 'No response data recorded.'}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPaymentTransactions;